import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { createProductTemplate } from 'api/operations/productServiceV2';
import {
  validateDateThatIsAfter,
  validateDateThatIsEqualOrAfter,
} from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import { firstDayOfYear } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { getRootStoreInstance } from 'store/rootStore';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductTemplate } from 'types/productServiceV2';
import ModalDelegate from 'store/general/modalDelegate';
import { getProductLinesByEffectiveDateAndState } from 'api/operations/domainServiceV2';

interface CreateTemplateFields extends DefaultFormFields {
  name: FormControl<string>;
  internalName: FormControl<string>;
  productLineId: FormControl<string>;
  state: FormControl<string>;
  effectiveFrom: FormControl<string>;
  effectiveTo: FormControl<string>;
}

export const createTemplateFormController = new FormController<CreateTemplateFields>({
  name: new FormControl<string>('', { optional: false, trim: true }),
  internalName: new FormControl<string>('', { optional: false, trim: true }),
  productLineId: new FormControl<string>('CustomDental', { optional: false }),
  state: new FormControl<string>('', { optional: false }),
  effectiveFrom: new FormControl<string>(firstDayOfYear(), {
    optional: false,
    customValidations: [
      {
        check: (value: string): boolean => validateDateThatIsEqualOrAfter(value, firstDayOfYear()),
        msg: 'Date cannot be earlier than the current year',
      },
    ],
  }),
  effectiveTo: new FormControl<string>('2099-01-01', {
    optional: false,
    customValidations: [
      {
        check: (value: string): boolean =>
          (!validateDateThatIsAfter(value, '2099-01-01') || value === '2099-01-01') &&
          validateDateThatIsEqualOrAfter(value, firstDayOfYear()),
        msg: 'Incorrect date picked',
      },
    ],
  }),
});

export class CreateTemplateModalStore extends ModalDelegate {
  private _rootStore = getRootStoreInstance();

  private _modalLoading = false;
  private _createTemplateForm: FormController<CreateTemplateFields>;
  private _availableProductLineIds: ProductLineId[] = [];
  private _availableStates: string[] = [];

  public get modalLoading(): boolean {
    return this._modalLoading;
  }

  private setModalLoading(value: boolean): void {
    this._modalLoading = value;
  }

  public get createTemplateForm(): FormController<CreateTemplateFields> {
    return this._createTemplateForm;
  }

  public get availableProductLineIdsOptions(): OptionType[] {
    return this._availableProductLineIds.map((pLineId) => ({
      value: pLineId,
      label: PRODUCT_LINES_CONTENT_MAP[pLineId].label,
    }));
  }

  public get availableStatesOptions(): OptionType[] {
    return this._availableStates.map((state) => ({
      value: state,
      label: state,
    }));
  }

  public async getInitialData(): Promise<void> {
    this._availableStates = this._rootStore.domainStoreV2.domainHeader.properties.eligibleStates || [];
    this._createTemplateForm.populateFields({ state: this._availableStates[0] || '' });
    await this.getAvailableProductLines();
  }

  private async getAvailableProductLines(): Promise<void> {
    const { effectiveFrom, state } = this._createTemplateForm.fields;
    if (!effectiveFrom.value || !state.value) {
      return;
    }

    this.setModalLoading(true);

    const domainProductLines = await getProductLinesByEffectiveDateAndState(
      this._rootStore.domainStoreV2.domainHeader.id,
      effectiveFrom.value,
      state.value,
    );

    runInAction(() => {
      // Show only product lines for Custom rate segment
      this._availableProductLineIds = domainProductLines
        .filter((pLine) => pLine.rateSegment === 'CUSTOM')
        .map((pLine) => pLine.productLineId)
        .sort(sortByProductLineId);

      this._createTemplateForm.populateFields({ productLineId: this._availableProductLineIds[0] || '' });
      this.setModalLoading(false);
    });
  }

  public async createProductTemplate(): Promise<ProductTemplate | null> {
    this.setModalLoading(true);
    const { name, internalName, productLineId, state, effectiveFrom, effectiveTo } = this._createTemplateForm.fields;

    const productTemplateInput = {
      productLine: productLineId.value as ProductLineId,
      productName: name.value,
      internalName: internalName.value,
      effectiveFrom: effectiveFrom.value,
      effectiveTo: effectiveTo.value,
      bc: state.value ? { state: [state.value] } : null,
      rateSegment: 'CUSTOM' as RateSegment,
    };

    const res = await createProductTemplate(productTemplateInput);
    return runInAction((): ProductTemplate | null => {
      this.setModalLoading(false);
      if (res.data?.createProductTemplate) {
        return res.data?.createProductTemplate;
      }
      return null;
    });
  }

  public resetForm(): void {
    this._createTemplateForm.resetForm();
    this._availableProductLineIds = [];
    this._availableStates = [];
  }

  constructor() {
    super();

    this._createTemplateForm = createTemplateFormController;

    makeObservable<
      CreateTemplateModalStore,
      | '_modalLoading'
      | '_createTemplateForm'
      | '_availableProductLineIds'
      | '_availableStates'
      | 'createTemplateForm'
      | 'availableProductLineIdsOptions'
      | 'setModalLoading'
    >(this, {
      _modalLoading: observable,
      _createTemplateForm: observable,
      _availableProductLineIds: observable,
      _availableStates: observable,

      modalLoading: computed,
      createTemplateForm: computed,
      availableStatesOptions: computed,
      availableProductLineIdsOptions: computed,

      createProductTemplate: action.bound,
      setModalLoading: action.bound,
      resetForm: action.bound,
      getInitialData: action.bound,
    });

    reaction(
      () => [this._createTemplateForm.fields.effectiveFrom.value, this._createTemplateForm.fields.state.value],
      async ([effectiveFrom, state], [prevEffectiveFrom, prevState]) => {
        const effectiveFromChanged = effectiveFrom !== prevEffectiveFrom;
        const stateChanged = state !== prevState;

        if (effectiveFromChanged || stateChanged) {
          await runInAction(async () => {
            await this.getAvailableProductLines();
          });
        }
      },
    );
  }
}
